import { Injectable } from '@angular/core';
import { MiaHttpService, ApiResponse } from '@mobileia/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable, of } from 'rxjs';
import { Category } from '../entities/category';
import { finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { readSessionCache, writeSessionCache } from '../utils/cache.utils';

@Injectable({
  providedIn: 'root'
})
export class CategoryService extends MiaHttpService {

  private static readonly CATEGORIES_ALL_CACHE_KEY = 'ph_categories_all_cache';
  private static readonly CATEGORIES_FEATURED_CACHE_KEY = 'ph_categories_featured_cache';
  private static readonly CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

  private categoriesAllCache: ApiResponse<Array<Category>> | null = null;
  private categoriesAllCacheAt = 0;
  private categoriesAllRequest$: Observable<ApiResponse<Array<Category>>> | null = null;

  private categoriesFeaturedCache: ApiResponse<Array<Category>> | null = null;
  private categoriesFeaturedCacheAt = 0;
  private categoriesFeaturedRequest$: Observable<ApiResponse<Array<Category>>> | null = null;

  constructor(
    private http: HttpClient
  ) {
    super(http);
  }

  fetchById(categoryId: number): Observable<Category> {
    return this.fetchAll().pipe(map(data => {
      if (!data.success) {
        return;
      }

      for (const category of data.response) {
        if (category.id == categoryId) {
          return category;
        }
        for(const subCategory of category.childs){
          if(subCategory.id == categoryId){
            return subCategory;
          }
          for(const categoryProduct of subCategory.childs){
            if(categoryProduct.id == categoryId){
              return categoryProduct;
            }
          }
        }
      }

      return;
    }));
  }

  fetchAll(): Observable<ApiResponse<Array<Category>>> {
    const now = Date.now();
    this.tryLoadAllCache();

    if (this.categoriesAllCache && now - this.categoriesAllCacheAt < CategoryService.CACHE_TTL_MS) {
      return of(this.categoriesAllCache);
    }

    if (this.categoriesAllRequest$) {
      return this.categoriesAllRequest$;
    }

    this.categoriesAllRequest$ = this.postArray(environment.baseUrl + 'category/fetch-public', { }).pipe(
      tap(data => {
        if (data && data.success) {
          this.saveAllCache(data);
        }
      }),
      finalize(() => this.categoriesAllRequest$ = null),
      shareReplay(1)
    );

    return this.categoriesAllRequest$;
  }

  fetchFeatured(): Observable<ApiResponse<Array<Category>>> {
    const now = Date.now();
    this.tryLoadFeaturedCache();

    if (this.categoriesFeaturedCache && now - this.categoriesFeaturedCacheAt < CategoryService.CACHE_TTL_MS) {
      return of(this.categoriesFeaturedCache);
    }

    if (this.categoriesFeaturedRequest$) {
      return this.categoriesFeaturedRequest$;
    }

    this.categoriesFeaturedRequest$ = this.postArray(environment.baseUrl + 'category/fetch-feature', { }).pipe(
      tap(data => {
        if (data && data.success) {
          this.saveFeaturedCache(data);
        }
      }),
      finalize(() => this.categoriesFeaturedRequest$ = null),
      shareReplay(1)
    );

    return this.categoriesFeaturedRequest$;
  }

  private tryLoadAllCache() {
    if (this.categoriesAllCache) {
      return;
    }
    const cached = readSessionCache<ApiResponse<Array<Category>>>(CategoryService.CATEGORIES_ALL_CACHE_KEY, CategoryService.CACHE_TTL_MS);
    if (cached) {
      this.categoriesAllCache = cached.data;
      this.categoriesAllCacheAt = cached.timestamp;
    }
  }

  private tryLoadFeaturedCache() {
    if (this.categoriesFeaturedCache) {
      return;
    }
    const cached = readSessionCache<ApiResponse<Array<Category>>>(CategoryService.CATEGORIES_FEATURED_CACHE_KEY, CategoryService.CACHE_TTL_MS);
    if (cached) {
      this.categoriesFeaturedCache = cached.data;
      this.categoriesFeaturedCacheAt = cached.timestamp;
    }
  }

  private saveAllCache(data: ApiResponse<Array<Category>>) {
    this.categoriesAllCache = data;
    this.categoriesAllCacheAt = Date.now();
    writeSessionCache(CategoryService.CATEGORIES_ALL_CACHE_KEY, data, this.categoriesAllCacheAt);
  }

  private saveFeaturedCache(data: ApiResponse<Array<Category>>) {
    this.categoriesFeaturedCache = data;
    this.categoriesFeaturedCacheAt = Date.now();
    writeSessionCache(CategoryService.CATEGORIES_FEATURED_CACHE_KEY, data, this.categoriesFeaturedCacheAt);
  }
}
